import { Task, TransferOptions } from "~/packages/taskrouter";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getReservationsByTask, isCallTask, sendTrackingEvent } from "../../ActionUtils";
import { EVENTS } from "~/modules/analytics/Analytics";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { Action } from "~/modules/actions/Action";




export interface TransferTaskOptions {
    transferOptions?: TransferOptions;
}




















export class StartVoiceTaskTransfer implements Action<Promise<Task>> {
    


    #taskSid: string;

    


    #to: string;

    


    #transferTaskOptions: TransferTaskOptions;

    constructor(taskSid: string, to: string, transferTaskOptions: TransferTaskOptions) {
        this.#taskSid = taskSid;
        this.#to = to;
        this.#transferTaskOptions = transferTaskOptions;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        logger.debug(`startVoiceTaskTransfer invoked with taskSid: ${this.#taskSid} and to: ${this.#to}`);

        const transferOptions = this.#transferTaskOptions?.transferOptions;

        if (!worker) {
            const errorMsg = "startVoiceTaskTransfer: worker is not initialized";
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");

        if (!reservation) {
            const errorMsg = `startVoiceTaskTransfer: Reservation for task ${this.#taskSid} is not found`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartVoiceTaskTransferCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!isCallTask(reservation.task)) {
            const errorMsg = `startVoiceTaskTransfer: Reservation ${this.#taskSid} is not an active call`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartVoiceTaskTransferCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode,
                reservationSid: reservation.sid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (reservation.task.transfers.outgoing?.status === "INITIATED") {
            const errorMsg = `startVoiceTaskTransfer: Reservation ${this.#taskSid} has already initiated transfer`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartVoiceTaskTransferCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode,
                reservationSid: reservation.sid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (reservation.workerSid !== worker.sid) {
            const errorMsg = `startVoiceTaskTransfer: Reservation ${
                this.#taskSid
            } cannot be transferred by a different worker`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartVoiceTaskTransferCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode,
                reservationSid: reservation.sid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            if (transferOptions?.mode === "COLD") {
                await reservation.updateParticipant({ endConferenceOnExit: false });
            }
            const task = await reservation?.task.transfer(this.#to, transferOptions!);

            sendTrackingEvent(EVENTS.StartVoiceTaskTransferCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode,
                reservationSid: reservation.sid
            });

            return task;
        } catch (error) {
            if (transferOptions?.mode === "COLD") {
                await reservation.updateParticipant({ endConferenceOnExit: true });
            }
            const errorMsg = `startVoiceTaskTransfer: Could not transfer task: ${error.message}`;
            sendTrackingEvent(EVENTS.StartVoiceTaskTransferCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode,
                reservationSid: reservation.sid
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }
}
